import type { ApplyResult, LengthUnit, Matrix, NodeRecord, Rect, Scene, SnapGuide, SnapSettings } from '@flobi/artwork-core';
import type * as Y from 'yjs';
import type { IconName } from '../chrome/icons';
import type { MenuEntry } from '../../ui/menu';
import type { ToolId } from '../useEditorCanvas';
import type { PathEditing } from '../pathEditing';
import type { Ruler, Subpath } from '@flobi/artwork-core';
import type { TextEditing } from '../textEditing';

/** A point in the document's own coordinates, which is what tools work in. */
export interface Point {
  readonly x: number;
  readonly y: number;
}

export interface EditorStyle {
  fill: string;
  /** False draws the shape hollow: the colour is kept, so turning it back on remembers it. */
  fillOn: boolean;
  /** The colour of the outline, which used to be a hardcoded near-black. */
  stroke: string;
  strokeWeight: number;
  opacity: number;
}

/**
 * What a tool may draw over the canvas.
 *
 * Everything is in document coordinates and every width is in screen pixels: a handle
 * is the same size to the hand at 20% zoom as at 400%, and a tool never has to know
 * about the camera, the device's pixel ratio or the theme.
 */
export interface Overlay {
  line(from: Point, to: Point, options?: { width?: number; alpha?: number }): void;
  /**
   * A rectangle in document coordinates: outlined, or filled with a wash.
   *
   * `dashed` is for a box that is not a selection — a slice, which is a window on the page
   * rather than a thing chosen on it. Two boxes drawn the same way say the same thing, and
   * these two mean opposite things.
   */
  /** A picture lying at `placed` in a layer's own units, faded, with `frame` left clear. */
  picture?(assetId: string, transform: Matrix, placed: Rect, frame: Rect, alpha: number): void;
  box(rect: Rect, options?: { fill?: boolean; alpha?: number; width?: number; dashed?: boolean }): void;
  /**
   * A closed region washed in, in its own shape rather than as the box around it.
   *
   * `dotted` stipples it instead of flooding it — the way a shape builder says "this
   * part", where a flat wash would hide the artwork the person is deciding about.
   */
  region(rings: readonly (readonly Point[])[], options?: { alpha?: number; width?: number; edge?: number; dotted?: boolean }): void;
  /** A square anchor: filled when it is chosen, hollow when it is not. */
  handle(at: Point, options?: { filled?: boolean; hovered?: boolean; size?: number }): void;
  /** A round handle, which is what a Bézier control point is drawn as. */
  knob(at: Point, options?: { hovered?: boolean }): void;
  /**
   * A round handle with an arrow in it, pointing the way it is pulled (`toward`, on the
   * page; `both` for an arrow both ways): a repeat's handles (`tools/repeatHandles.ts`).
   */
  grip(at: Point, options: { toward: Point; both?: boolean; hovered?: boolean }): void;
  /** A path through points, straight or curved; the curve's controls are absolute. */
  curve(points: readonly { at: Point; in?: Point; out?: Point }[], options?: { width?: number }): void;
  /**
   * A short line of type on the glass: a measurement, a name, an angle.
   *
   * `at` is in document coordinates and everything else is in screen pixels, so a label
   * stays the same size however far the page is zoomed — it is the editor talking, not
   * part of the drawing, and type that grew with the artwork would read as part of it.
   *
   * Drawn on a plate of the selection colour, because a measurement has to be legible
   * over whatever it is measuring, and artwork is the one thing guaranteed to be under it.
   */
  label(
    at: Point,
    text: string,
    options?: {
      size?: number;
      align?: 'left' | 'center' | 'right';
      baseline?: 'top' | 'middle' | 'bottom';
      /** Moved by this many screen pixels after placing, to sit clear of what it names. */
      offset?: Point;
      /** `quiet` is type with no plate, for a label that is not the point of the picture. */
      tone?: 'accent' | 'quiet';
    },
  ): void;
  /**
   * A snap guide: the line that says why a shape stopped where it did. It is drawn in
   * its own colour rather than the selection's, because it is not part of the selection
   * and reads as noise if it looks like one.
   */
  guide(from: Point, to: Point): void;
}

/**
 * Everything a tool is allowed to do to the editor.
 *
 * A tool gets the document, the selection and a way to change both, and nothing else:
 * no renderer, no camera, no React. That is what makes a new tool a file rather than a
 * change to the canvas.
 */
export interface ToolContext {
  readonly page: Y.Doc;
  readonly scene: Scene;
  readonly selection: readonly string[];
  readonly style: EditorStyle;
  /** How many document units a screen pixel is, for distances that must feel constant. */
  readonly unitsPerPixel: number;
  /**
   * How many presses in a row the press being handled is: 1, 2 for a double, 3 for a
   * triple.
   *
   * Counted by the canvas rather than read off the event, because a PointerEvent's
   * `detail` is 0 by specification — the click count lives on MouseEvent, which is not
   * what a canvas listens to. Anything that reads `event.detail` here is reading a zero.
   */
  readonly clicks: number;

  /** Runs engine commands as this person, which is what undo takes back. */
  edit(ops: unknown[]): ApplyResult;
  setSelection(ids: string[]): void;
  /** Closes the current undo step, so the next edit starts a new one. */
  stopCapturing(): void;
  /** Tells the person, briefly, why something they asked for did not happen. */
  notice(message: string): void;
  /** The tool is finished; the editor goes back to select. */
  finish(): void;
  /** Ask for a frame, when something the tool draws has changed. */
  refresh(): void;

  /** The topmost node at a point, ignoring artboards and locked nodes. */
  hit(world: Point): string | null;
  /** The deepest layer drawn on top at a point, whatever groups it sits in: a ⌘-click. */
  deepHit?(world: Point): string | null;
  /** The artboard whose name, drawn over its top left corner, is under a point; a press there takes the board. */
  boardLabelAt?(world: Point): string | null;
  /** Draws these artboards from one sliding picture while a drag carries them; null puts them back. */
  liftBoards?(ids: readonly string[] | null): void;
  /** A picture's size in pixels, or null while nothing says yet. */
  pictureSize?(assetId: string): { width: number; height: number } | null;
  /** The layer whose picture is being placed in its frame changed. */
  croppingChanged?(id: string | null): void;
  /** Enter a normal group and select its child under the pointer. */
  enterGroup?(id: string, world: Point): boolean;
  readonly selectionScope?: string | null;
  /**
   * Change what this tool draws with: its colour, its weight, its opacity.
   *
   * What the tool draws NEXT, and nothing else. The rail is the tool's, so a control on
   * it reaches no further than the tool — repainting something already on the page is
   * the inspector's job. That is the same reason select has no style block: choosing a
   * thing and deciding what it looks like are different acts.
   */
  drawWith(patch: Partial<EditorStyle>): void;
  /** This tool's own settings changed, so the rail must be read again. */
  railChanged(): void;
  /**
   * Puts another tool in hand, as pressing its mark in the bar would.
   *
   * For a family of tools that are one tool to the person using them — the five shapes —
   * so the rail can offer all of them whichever is in hand, instead of the kinds hiding
   * in a menu over the bar.
   */
  chooseTool(id: ToolId): void;
  /**
   * Copies whatever is chosen and chooses the copies, answering with their ids.
   *
   * `offset` is how far off the originals they land — none, for a gesture that is about
   * to carry them somewhere itself. The undo step is left open, so the copy and the drag
   * that follows it are one thing to take back rather than two.
   */
  duplicate(offset?: number): string[];
  /**
   * After a drag: moves each dropped layer into the artboard it now sits on, or out onto
   * the page when it sits on none, keeping it exactly where it is. Part of the drag's own
   * undo step, so call it before `stopCapturing`.
   */
  settleIntoArtboards(ids: readonly string[]): void;
  /** Show the fields panel, for when a gesture has asked a question only numbers answer. */
  showFields(): void;
  /** What is held down right now, whether or not an event has just arrived. */
  readonly modifiers: { readonly ctrl: boolean; readonly shift: boolean; readonly alt: boolean; readonly meta: boolean };
  /** The unit the document is thought in: what every number a tool shows is said in. */
  readonly units: LengthUnit;
  /**
   * What the pointer should look like here. Any CSS cursor, or null for the default.
   *
   * A tool says this rather than reaching for the canvas element, the same bargain as
   * `draw` and `rail`: the cursor is part of what a tool means, and the canvas is the
   * only thing that knows where to put it.
   */
  setCursor(css: string | null): void;

  /**
   * What a drag is allowed to be pulled by: the grid, the guides, other shapes, whole
   * pixels — whichever of the four the person has switched on.
   *
   * A tool asks for this and hands it to `snapDrag` rather than deciding for itself,
   * which is what makes one set of toggles mean the same thing to every tool. A tool
   * that snapped on its own terms would be a tool the toggles do not reach.
   */
  readonly snap: SnapSettings;
  /** The guides a drag may land on: the ones that are shown, in document coordinates. */
  readonly guides: readonly SnapGuide[];
  /**
   * Where a point being PLACED should actually land.
   *
   * The one call every tool that puts something down makes: a corner being dragged out,
   * an anchor going in, a handle being sized. It asks the same four rules a moved shape
   * asks — a guide, another shape's corner or middle, the grid, a whole unit — and hands
   * back the answer, having already told the canvas which lines to draw to explain it.
   *
   * It exists because the alternative is every tool gathering its own candidates and
   * deciding its own precedence, and then eight tools snap eight slightly different
   * ways. Drawing a rectangle and moving one afterwards have to agree about what counts
   * as lined up, or the second act undoes the care taken in the first.
   *
   * `ignore` is whatever the gesture is carrying — a shape being drawn must not line
   * itself up with itself. `axes` limits it to the axes the gesture can change: an edge
   * handle moves one, and a line lit on the other would claim a match it cannot make.
   */
  place(world: Point, ignore?: readonly string[], axes?: { x: boolean; y: boolean }): Point;
  /**
   * Says which guides the drag is landing on, so the canvas can draw them as having
   * caught something.
   *
   * The guide itself is already on screen, which is exactly why this is needed: without
   * it a drag stopping on a guide looks the same as a drag stopping for no reason. Pass
   * an empty list when the gesture ends.
   */
  catchGuides(on: readonly SnapGuide[]): void;

  /** The artboard a point is over, which is what a new shape is created inside. */
  artboardAt(world: Point): string | undefined;
  /** Brings a box into view if it is not already — the whole page refitted, as Add Artboard does. */
  reveal(box: Rect): void;
  /** Every node whose box meets a world rectangle: what a drag can line itself up with. */
  nearby(box: Rect): readonly string[];
  /**
   * What a drag of `moving` can line itself up with near a box: `nearby`, less the
   * shapes being moved, what they carry, and what CARRIES them — a group holding a
   * moving shape is opened up into its other layers instead.
   *
   * A group's box is where its moving child was a moment ago, so snapping to it made a
   * child inside a group trail the pointer in ~7 px steps and land short
   * (QA 2026-09-24, view-snapping-F3).
   */
  snapNeighbours(box: Rect, moving: readonly string[]): readonly string[];
  /** A node's box in document coordinates, strokes and effects included. */
  boundsOf(id: string): Rect | null;
  /**
   * A path node measured out by distance, in world coordinates.
   *
   * What a tool needs to answer "how far along this curve did the press land" — the one
   * question type on a path is founded on.
   */
  rulerOf(id: string): Ruler | null;
  /**
   * One outline from many, computed exactly by the renderer's own path arithmetic.
   *
   * A tool that paints or cuts needs real geometry, not a stack of pieces that happen to
   * overlap — and this engine already carries a library that does it properly. Answers in
   * world coordinates, and folds away a ring that crosses itself along the way, which is
   * what an offset outline does wherever the brush is wider than the turn it is taking.
   */
  unite(rings: readonly (readonly Point[])[], others: readonly string[]): { subpaths: Subpath[]; merged: string[] } | null;
  /**
   * A node's outline cut by regions, in world coordinates.
   *
   * `inside[i]` is what the node has within region `i` — which is what a knife's two
   * halves are — and `outside` is what it has beyond all of them, which is what an eraser
   * leaves. Both come from the renderer's own path arithmetic, so the pieces fit back
   * together exactly rather than nearly.
   *
   * `together` treats the regions as one area (an eraser's brush): `inside` then has one
   * entry, what the node has under all of them, and the union is worked out once for a run
   * of calls with the same regions rather than once per region per node.
   */
  carve(id: string, regions: readonly (readonly Point[])[], options?: { together?: boolean }): { inside: Subpath[][]; outside: Subpath[] } | null;
  /**
   * A shape-builder session over a set of shapes.
   *
   * Regions are asked for one at a time rather than all at once, because a region is
   * cheap to name — it is the set of shapes covering a place — and expensive to build.
   * With a hundred shapes there are hundreds of regions and almost none of them will ever
   * be pointed at, so `at` works one out only when the hand arrives over it, and keeps it.
   *
   * `open` says which shapes are in play and answers with the ones it could use. `at`
   * gives the region under a point: its `key` names it, so two points in the same region
   * come back with the same one. `commit` unions the regions named and subtracts them
   * from every original, answering with whatever new shape it made.
   */
  readonly build: {
    open(ids: readonly string[]): string[];
    outlines(): { id: string; rings: Point[][] }[];
    at(world: Point): { key: string; rings: Point[][]; owners: string[] } | null;
    /**
     * Unions the regions named, subtracts them from every original, and answers with
     * what was made and with EVERYTHING still in play — the survivors as well.
     *
     * A shape builder is used in a run of strokes. A commit that answered only with its
     * own result would leave the tool holding one shape and nothing to build against,
     * which is a tool that works once.
     */
    commit(keys: readonly string[], how: 'merge' | 'erase'): { built: string[]; playing: string[]; outline: Point[][] };
    /** Every piece a shape of its own — or, with `overlapsOnly`, only the shared ones kept. */
    split(overlapsOnly: boolean): { built: string[]; playing: string[]; outline: Point[][] };
    /** Touching shapes with the same paint joined into one. */
    mergeByColour(): { built: string[]; playing: string[]; outline: Point[][] };
    close(): void;
  };
  node(id: string): NodeRecord | undefined;

  /** Typing into a text layer, and editing a path's points: shared by several tools. */
  readonly text: TextEditing | null;
  readonly points: PathEditing;
}

/**
 * The rail down the left edge, as a tool asks for it.
 *
 * Two wells: how the tool works above, what can be done with the result below. A well
 * holds one slider or up to four buttons — four because that is what fits: 4 × 33 px with
 * 4 px between them is the well's 144 px exactly.
 *
 * The colour, undo and redo used to sit here too. They went because all three are reachable
 * without the rail — the colour from the bar, the other two from the keys — and the rail is
 * the one surface that answers to the tool in hand. Anything that means the same thing
 * whatever is in hand does not belong on it.
 *
 * A tool declares this and never draws it, the same bargain as `draw(overlay, ctx)`.
 * That is what lets the thirty-first tool arrive without a line of sidebar code changing.
 */
export interface Rail {
  /** Live context for the dedicated point editor; coordinates refer to a single anchor. */
  readonly pointEditing?: {
    readonly selected: number;
    readonly total: number;
    readonly closed: boolean;
    readonly coordinates?: readonly RailField[];
  };
  /**
   * What the tool in hand is working on, when that is not the tool itself: the side panel
   * heads its options with it. An effector is chosen with the Cloner in hand, and its
   * numbers under the heading "Cloner" said nothing about what was being turned.
   */
  readonly title?: string;
  readonly top?: Well;
  /**
   * A second quantity, for the few tools that genuinely have two.
   *
   * Most do not, and a rail with three wells plus the style block is as tall as this
   * frame will take — so this is for a shape like the star, which is not describable by
   * one number: how many points it has and how deep they cut are different questions.
   */
  readonly middle?: Well;
  readonly bottom?: Well;
  /**
   * Whether the fill, the outline and its size belong under this tool's wells.
   *
   * A tool that DRAWS asks for them; select does not, because choosing something is not
   * the same act as deciding what it will look like. The rail changes with the tool, so
   * a control that only some tools mean has to be one of the things that changes.
   */
  readonly style?: boolean;
}

export type Well =
  | {
      readonly kind: 'slider';
      readonly icon: IconName;
      readonly title: string;
      readonly value: number;
      readonly min: number;
      readonly max: number;
      readonly step?: number;
      /** How the number under the slider reads, when it is not just the number. */
      readonly format?: (value: number) => string;
      /**
       * How typed text turns back into a value, for a well whose `format` is not just the
       * number. The options bar shows `format` in its number box only alongside this: a
       * box reading "-40%" that took a typed 40 as forty whole units clamped it to +200%
       * (QA 2026-09-24, cloner-F13). Null refuses what was typed.
       */
      readonly parse?: (text: string) => number | null;
      onChange(value: number): void;
    }
  | { readonly kind: 'buttons'; readonly items: readonly RailButton[] }
  /**
   * A choice from a long list — the artboard tool's sizes — as a button that names the
   * current one and opens a menu. For lists too long for a row of marks and too
   * structured (groups, sizes beside names) for a plain pop-up.
   */
  | {
      readonly kind: 'menu';
      /** Left off when the `label` already says what it is: a mark and a word for one thing is one too many. */
      readonly icon?: IconName;
      readonly title: string;
      readonly value: string;
      readonly items: readonly MenuEntry[];
      /**
       * What the choice is a choice OF, in front of its value — "Where  Around a spot" —
       * for a pop-up that is one of several on a strip and would otherwise be an answer
       * with no question.
       */
      readonly label?: string;
      /** A menu of things to DO rather than a value to choose: no chevron beside the name. */
      readonly action?: boolean;
    }
  /**
   * Several quantities at once, each named and each showing its number — for a tool whose
   * settings are all in use together and would otherwise be reached one at a time through
   * a picker and a single slider, with no way to see which of them were set.
   */
  | { readonly kind: 'fields'; readonly items: readonly RailField[] }
  /**
   * Wells laid out as one: several things that belong in a single place on the strip.
   * A rail has three wells and no more; this is how one of them holds a row of parts.
   */
  | { readonly kind: 'group'; readonly wells: readonly Well[] }
  /**
   * Where the strip starts a new line. The side panel, which is one column already,
   * ignores it.
   */
  | { readonly kind: 'break' }
  /** A name, standing where a control would: what the controls beside it are about. */
  | { readonly kind: 'text'; readonly text: string };

/** One named quantity in a `fields` well. */
export interface RailField {
  readonly id: string;
  /** Its name, said in front of the number. */
  readonly label: string;
  /** "Name — what it means", for the help tag. */
  readonly title: string;
  readonly value: number;
  readonly min: number;
  readonly max: number;
  readonly step?: number;
  /** How the number reads, when it is not just the number. */
  readonly format?: (value: number) => string;
  /** And how typed text turns back into a value, for a field whose `format` adds words. */
  readonly parse?: (text: string) => number | null;
  onChange(value: number): void;
}

/**
 * A well, flattened: its groups opened out into the wells they hold, in order.
 *
 * The strip and the panel both draw a rail as a list of parts, and a group is only how a
 * tool says "these go together" — so neither of them should have to know it is one.
 */
export function partsOf(wells: readonly (Well | undefined)[]): Well[] {
  const out: Well[] = [];
  for (const well of wells) {
    if (!well) continue;
    if (well.kind === 'group') out.push(...partsOf(well.wells));
    else out.push(well);
  }
  return out;
}

export interface RailButton {
  readonly id: string;
  readonly icon: IconName;
  readonly title: string;
  /**
   * Its name, written on the button. A mark alone is fine for what everybody knows —
   * a trash can — and not for what nobody does: an effector's first button was a
   * four-way arrow, and nobody who had not been told what it did would have pressed it.
   */
  readonly label?: string;
  /** With a `label`: its name alone on the strip, without the mark (the panel keeps it). */
  readonly bare?: boolean;
  readonly pressed?: boolean;
  readonly disabled?: boolean;
  /**
   * The one choice this button belongs to, when a well holds several — the Cloner's
   * arrangement, an effector's kind and its field. Buttons sharing a group are drawn as
   * one segmented control; buttons with none are marks of their own.
   *
   * Without it every `pressed` button in a well became one control, which could show
   * only one of them chosen: Grid and Linear read as one choice with the Follow switch
   * and the effector's kinds (QA 2026-09-24, cloner-F5).
   */
  readonly group?: string;
  /** Visual sections in the side panel; choices still share one active mode. */
  readonly panelGroup?: string;
  /** Show the affected canvas geometry while the action is hovered or keyboard-focused. */
  readonly preview?: (shown: boolean) => void;
  /**
   * `more` is command or control held: the same action asked for harder.
   *
   * Only for buttons where that reading is obvious — grow one ring against grow all the
   * way — and the title always says so, because a modifier nobody is told about is a
   * feature nobody has.
   */
  run(options: { more: boolean }): void;
}


/**
 * A tool: what happens between a press and a release, and what is drawn while it does.
 *
 * Returning true from a pointer or key handler means the tool took it, so the editor
 * leaves it alone — which is how a tool keeps the Delete key while it is using it.
 */
export interface Tool {
  readonly id: string;
  /** The tool was chosen. */
  activate?(ctx: ToolContext): void;
  /** Another tool was chosen, or the file closed: leave nothing behind. */
  deactivate?(ctx: ToolContext): void;

  onPointerDown?(event: PointerEvent, world: Point, ctx: ToolContext): boolean | void;
  onPointerMove?(event: PointerEvent, world: Point, ctx: ToolContext): void;
  onPointerUp?(event: PointerEvent, world: Point, ctx: ToolContext): void;
  onDoubleClick?(event: MouseEvent, world: Point, ctx: ToolContext): boolean | void;
  onKeyDown?(event: KeyboardEvent, ctx: ToolContext): boolean | void;
  /** A key let go: a modifier held during a gesture changes what it does when it is lifted too. */
  onKeyUp?(event: KeyboardEvent, ctx: ToolContext): void;
  /**
   * The path whose points were open has closed and no other was opened in its place,
   * whatever closed it: a key, a click off it, the selection moving elsewhere, the path
   * going. A tool that has a way of working only for points goes back to its ordinary
   * one, as its own Done would (QA 2026-09-25, select-points-F9).
   */
  onPointsClosed?(ctx: ToolContext): void;
  /**
   * The pointer left the canvas, or the system took the press away (a gesture, a palm):
   * whatever follows the hand goes, and a stroke in progress is abandoned rather than
   * finished (QA 2026-09-25, pencil-fill-brush-F6, F14).
   */
  onPointerLeave?(ctx: ToolContext): void;
  onPointerCancel?(ctx: ToolContext): void;

  /**
   * True while the tool is in the middle of something that spans several presses — the
   * pen drawing a path. Every press and every command closes the undo step before it
   * starts, so one Cmd+Z takes back one thing; while this is true a press does not, and
   * the tool closes the step itself when it is done.
   */
  holdsStep?(): boolean;

  /** Starts or ends placing a layer's picture in its frame; the Select tool's way of working. */
  cropPicture?(id: string | null, ctx: ToolContext): void;

  /** Drawn over the canvas every frame, after the document and before the peers. */
  draw?(overlay: Overlay, ctx: ToolContext): void;

  /**
   * What the rail shows while this tool is in hand. Read when the tool changes, when the
   * selection changes and when the tool says its own settings have — so it may depend on
   * all three, which is what makes the rail answer to the work rather than to the tool.
   */
  rail?(ctx: ToolContext): Rail | null;
}
